import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Calculator } from "lucide-react";
import useSeo from "@/lib/useSeo";

const plans = [
  {
    name: "Starter",
    rate: "$950",
    note: "flat monthly fee",
    min: "For collections under $45K/month",
  },
  {
    name: "Single Location Office",
    rate: "2.25%",
    note: "of monthly insurance collections",
    min: "For single-location practices above $45K/month",
  },
  {
    name: "Office with Two or More Locations",
    rate: "2%",
    note: "of monthly insurance collections",
    min: "For multi-location practices above $45K/month",
  },
  {
    name: "Groups & DSOs",
    rate: "1.80%",
    note: "of monthly insurance collections",
    min: "For groups and DSOs above $45K/month",
  },
];

const ivPricing = [
  { service: "Full benefits breakdown (with history)", price: "$7.50" },
  { service: "Eligibility check", price: "$2.25" },
  { service: "Same-day full breakdown", price: "$9.00" },
  { service: "Same-day eligibility check", price: "$2.75" },
  { service: "Failed eligibility check", price: "$0.75" },
  { service: "Setup cost", price: "$0.00" },
];

const calcTier = (v, type) => {
  if (v < 45000) return { tier: "Starter", rate: "Flat monthly fee", fee: 950 };
  if (type === "multiple") return { tier: "Office with Two or More Locations", rate: "2%", fee: v * 0.02 };
  if (type === "dso") return { tier: "Groups & DSOs", rate: "1.80%", fee: v * 0.018 };
  return { tier: "Single Location Office", rate: "2.25%", fee: v * 0.0225 };
};

const fmt = (n) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });

export default function Pricing() {
  useSeo({
    title: "Dental RCM Pricing | Transparent Rates from 1.80% | ClariDent RCM",
    description: "Simple, transparent dental RCM pricing: a flat $950/month under $45K, or 1.80%–2.25% of monthly insurance collections. $0 setup fee. Estimate your rate with our calculator.",
    path: "/pricing",
  });
  const [collections, setCollections] = useState(80000);
  const [practiceType, setPracticeType] = useState("single");
  const result = calcTier(collections, practiceType);

  const practiceOptions = [
    { id: "single", label: "Single Location" },
    { id: "multiple", label: "Multiple Locations" },
    { id: "dso", label: "DSO / Group" },
  ];

  return (
    <main data-testid="pricing-page">
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Pricing</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter font-black text-navy-900" data-testid="pricing-heading">
            Transparent pricing. <span className="text-teal-600">No surprises.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Dental RCM expertise without the enterprise overhead. Percentage-of-collections pricing means we only earn when you get paid.
          </p>
        </div>
      </section>

      {/* PLANS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
        {plans.map((p, i) => (
          <motion.div
            key={p.name}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            className="group rounded-3xl border border-slate-200 transition-all duration-300 hover:border-teal-500 hover:shadow-[0_0_45px_rgba(14,143,143,0.35)] hover:-translate-y-1"
          >
            <div className="rounded-3xl p-10 h-full bg-white flex flex-col" data-testid={`plan-card-${i}`}>
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 min-h-[2.5rem]">{p.name}</p>
              <p className="font-display text-6xl font-black text-navy-900 mt-4">{p.rate}</p>
              <div className="min-h-[5.5rem]">
                <p className="text-sm text-slate-500 mt-1">{p.note}</p>
                <p className="text-sm font-bold text-navy-900 mt-2">{p.min}</p>
              </div>
              <div className="mt-auto pt-8">
                <Link to="/book" data-testid={`plan-cta-${i}`} className="block text-center rounded-full px-8 py-3.5 text-sm font-bold border-2 border-navy-900 text-navy-900 transition-all duration-200 group-hover:bg-teal-600 group-hover:border-teal-600 group-hover:text-white group-hover:shadow-lg">
                  Start With a Free Audit
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </section>

      {/* SAVINGS CALCULATOR */}
      <section id="calculator" className="max-w-7xl mx-auto px-6 lg:px-8 pb-20" data-testid="savings-calculator">
        <div className="rounded-3xl bg-navy-950 px-8 py-14 sm:px-14 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(14,143,143,0.25),transparent_60%)]" />
          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <Calculator size={24} className="text-teal-400" />
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-400">Instant Estimate</p>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white" data-testid="calculator-heading">
                See your rate in seconds.
              </h2>
              <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
                Enter your practice's average monthly insurance collections and see exactly which plan applies and what your estimated monthly investment would be.
              </p>
              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Your practice setup</p>
                <div className="flex flex-wrap gap-3" data-testid="practice-type-options">
                  {practiceOptions.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setPracticeType(o.id)}
                      data-testid={`practice-option-${o.id}`}
                      className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-200 ${
                        practiceType === o.id
                          ? "bg-teal-600 text-white shadow-lg"
                          : "border border-slate-700 text-slate-300 hover:border-teal-500 hover:text-teal-400"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <label htmlFor="collections-input" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mt-7">Monthly insurance collections</label>
                <div className="mt-3 flex items-center gap-4">
                  <span className="text-teal-400 font-display font-bold text-2xl">$</span>
                  <input
                    id="collections-input"
                    type="number"
                    min="0"
                    step="5000"
                    value={collections}
                    onChange={(e) => setCollections(Math.max(0, Number(e.target.value) || 0))}
                    className="w-44 rounded-xl border border-slate-700 bg-navy-900 px-4 py-3 text-xl font-display font-bold text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="savings-input"
                  />
                </div>
                <input
                  type="range"
                  min="10000"
                  max="3000000"
                  step="5000"
                  value={Math.min(Math.max(collections, 10000), 3000000)}
                  onChange={(e) => setCollections(Number(e.target.value))}
                  className="mt-6 w-full accent-teal-500"
                  data-testid="savings-slider"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>$10K</span><span>$3M</span>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-700 bg-navy-900/70 p-10 text-center" data-testid="savings-result">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-400" data-testid="savings-tier">{result.tier}</p>
              <p className="font-display text-6xl font-black text-white mt-4" data-testid="savings-fee">
                ${fmt(result.fee)}
                <span className="text-xl text-slate-400 font-semibold">/mo</span>
              </p>
              <p className="text-sm text-slate-400 mt-2" data-testid="savings-rate">
                {result.rate}{result.rate.includes("%") ? ` of $${fmt(collections)} collections` : " — collections under $45K"}
              </p>
              <p className="text-xs text-slate-500 mt-6">Estimate only. Your free RCM audit confirms the exact fit for your practice.</p>
              <Link to="/book" data-testid="savings-audit-btn" className="mt-6 inline-block rounded-full bg-teal-600 px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-1 hover:shadow-xl">
                Get Your Free RCM Audit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* IV PRICING */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-teal-600 mb-4">Insurance Verification Service</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-bold text-navy-900" data-testid="iv-heading">Pay per verification. Not a dollar more.</h2>
            <ul className="mt-8 space-y-4 text-sm sm:text-base text-slate-600">
              {[
                "We verify your schedule in advance — before patients walk in",
                "Full breakdowns and eligibility records saved in your document center",
                "Weekly client meetings and quick-response support included",
                "$450/month minimum — waived for practices on our billing service",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3"><CheckCircle2 size={20} className="text-teal-600 shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="iv-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy-950 text-white text-left">
                  <th className="px-6 py-4 font-display font-semibold">Verification Service</th>
                  <th className="px-6 py-4 font-display font-semibold">Price</th>
                </tr>
              </thead>
              <tbody>
                {ivPricing.map((r, i) => (
                  <tr key={r.service} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-6 py-4 text-slate-600">{r.service}</td>
                    <td className="px-6 py-4 font-bold text-navy-900">{r.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-16 rounded-3xl bg-navy-950 px-8 py-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(14,143,143,0.25),transparent_65%)]" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">See your savings before you sign anything.</h2>
            <p className="mt-4 text-slate-400 text-sm sm:text-base">Start with a free RCM audit — we'll show you what you're losing and what you'd save.</p>
            <Link to="/book" data-testid="pricing-cta-btn" className="mt-8 inline-block rounded-full bg-teal-600 px-10 py-4 text-sm font-bold text-white transition-all duration-200 hover:bg-teal-500 hover:-translate-y-1 hover:shadow-2xl">
              Get My Free RCM Audit
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
